import type { RouteRecordRaw } from "vue-router";
import type { Component } from "vue";
import { defineComponent, h } from "vue";
import { resolveLocalizedTitle } from "@/locales/route-title";
import type { MenuRoute, RouteItem } from "@/shared/types/route";

const pageModules = import.meta.glob<Component>("../../pages/**/*.vue", { import: "default" });

const notFoundPageComponent = () =>
  import("@/pages/result/NotFoundPage.vue").then((module) => module.default);
const layoutComponent = () => import("@/widgets/app-shell/AppShell.vue");
const routeViewPageComponent = () => import("./RouteView.vue").then((module) => module.default);
const namedRouteComponentCache = new Map<string, () => Promise<Component>>();

const migratedComponentAliases: Record<string, string> = {
  "/onWarehouse/product/batch": "/operations/basic-archive/batch",
  "/onWarehouse/product/batch/index": "/operations/basic-archive/batch/index",
  "/onWarehouse/product/record": "/operations/basic-archive/product",
  "/onWarehouse/product/record/index": "/operations/basic-archive/product/index",
  "/system/basicConfiguration/miniprogram": "/system/basic-configuration/miniprogram",
  "/system/basicConfiguration/miniprogram/index": "/system/basic-configuration/miniprogram/index",
  "/system/basicConfiguration/warehouse": "/system/basic-configuration/warehouse",
  "/system/basicConfiguration/warehouse/index": "/system/basic-configuration/warehouse/index",
};

function getTitle(route: RouteItem) {
  return resolveLocalizedTitle(route.meta.title, route.name);
}

function normalizeRootPath(path: string) {
  return path.startsWith("/") ? path : `/${path}`;
}

function joinPath(parentPath: string, path: string) {
  if (path.startsWith("/")) {
    return path;
  }

  return `${parentPath}/${path}`.replaceAll("//", "/");
}

function normalizeMigratedComponentPath(component: string) {
  const normalized = component.startsWith("/") ? component : `/${component}`;
  return migratedComponentAliases[normalized] || normalized;
}

function resolvePageModule(component: string) {
  const aliasedComponent = normalizeMigratedComponentPath(component);
  const candidates = [
    `../../pages${aliasedComponent}.vue`,
    `../../pages${aliasedComponent}/index.vue`,
  ];

  return {
    aliasedComponent,
    moduleKey: candidates.find((candidate) => candidate in pageModules),
  };
}

function getRouteComponentKey(component?: RouteItem["component"]) {
  if (!component) {
    return "NOT_FOUND";
  }

  if (typeof component !== "string") {
    return "INLINE_COMPONENT";
  }

  const normalizedComponent = component.toUpperCase();

  if (normalizedComponent === "LAYOUT") {
    return "LAYOUT";
  }

  if (normalizedComponent === "ROUTE_VIEW" || normalizedComponent === "BLANK") {
    return "ROUTE_VIEW";
  }

  const { aliasedComponent, moduleKey } = resolvePageModule(component);
  return moduleKey ? `PAGE:${moduleKey}` : `NOT_FOUND:${aliasedComponent}`;
}

function createNamedRouteComponent(
  routeName: string,
  componentKey: string,
  loadComponent: () => Promise<Component>,
) {
  const cacheKey = `${routeName}:${componentKey}`;
  const cachedComponent = namedRouteComponentCache.get(cacheKey);

  if (cachedComponent) {
    return cachedComponent;
  }

  let resolvedComponent: Promise<Component> | undefined;
  const namedComponentLoader = () => {
    resolvedComponent ??= loadComponent().then((component) =>
      defineComponent({
        name: routeName,
        inheritAttrs: false,
        setup(_, { attrs, slots }) {
          return () => h(component, attrs, slots);
        },
      }),
    );

    return resolvedComponent;
  };

  namedRouteComponentCache.set(cacheKey, namedComponentLoader);
  return namedComponentLoader;
}

function resolveComponent(route: RouteItem) {
  const component = route.component;

  if (!component) {
    return createNamedRouteComponent(route.name, "NOT_FOUND", notFoundPageComponent);
  }

  const normalizedComponent = typeof component === "string" ? component.toUpperCase() : component;

  if (normalizedComponent === "LAYOUT") {
    return layoutComponent;
  }

  if (normalizedComponent === "ROUTE_VIEW" || normalizedComponent === "BLANK") {
    return createNamedRouteComponent(route.name, "ROUTE_VIEW", routeViewPageComponent);
  }

  if (typeof component !== "string") {
    return component;
  }

  const { moduleKey } = resolvePageModule(component);
  const componentKey = getRouteComponentKey(component);
  return createNamedRouteComponent(
    route.name,
    componentKey,
    moduleKey ? pageModules[moduleKey] : notFoundPageComponent,
  );
}

function sortRoutes(routeList: RouteItem[]) {
  return [...routeList].sort((left, right) => (left.meta.orderNo || 0) - (right.meta.orderNo || 0));
}

function transformRoute(route: RouteItem, isRoot = false): RouteRecordRaw {
  return {
    path: isRoot ? normalizeRootPath(route.path) : route.path,
    name: route.name,
    redirect: route.redirect,
    component: resolveComponent(route),
    meta: {
      ...route.meta,
      title: getTitle(route),
      localizedTitle: route.meta.title,
      routeComponentKey: getRouteComponentKey(route.component),
    },
    children: route.children
      ? sortRoutes(route.children).map((child) => transformRoute(child))
      : undefined,
  } as RouteRecordRaw;
}

export function transformRoutes(routeList: RouteItem[]): RouteRecordRaw[] {
  return sortRoutes(routeList).map((route) => transformRoute(route, true));
}

function toMenuRoute(route: RouteItem, parentPath = ""): MenuRoute {
  const path = parentPath ? joinPath(parentPath, route.path) : normalizeRootPath(route.path);

  return {
    path,
    name: route.name,
    title: getTitle(route),
    icon: route.meta.icon,
    expanded: route.meta.expanded,
    redirect: route.redirect,
    children: route.children
      ? sortRoutes(route.children)
          .filter((child) => !child.meta.hidden)
          .map((child) => toMenuRoute(child, path))
      : undefined,
  };
}

export function toMenuRoutes(routeList: RouteItem[]): MenuRoute[] {
  return sortRoutes(routeList)
    .filter((route) => !route.meta.hidden)
    .map((route) => toMenuRoute(route));
}
